import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { axInst } from "../../../Components/Login/Service";
import { useSelector } from "react-redux";
import { Loader } from "rsuite";
import WaterMark from "../../../Components/WaterMark/WaterMark.jsx";

const gtmeTableApi = async (body) => {
  try {
    const res = await axInst.post("dealer-review/dealer_review_gtme", body);
    return res.data;
  } catch (error) {
    console.error("Error fetching GTME data:", error);
    throw error;
  }
};

const GtmeTable = ({ productLine = null }) => {
  const months = [
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
    "Jan",
    "Feb",
    "Mar",
  ];

  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState();
  const [selOps, setSelOps] = useState({ Product_Line: [] });

  const { data, submitted } = useSelector((e) => e.filtersDealerReview);

  const isOtherFilterApplied = (data) => {
    const keysToCheck = Object.keys(data).filter(
      (key) => key !== "Product_Line",
    );
    return keysToCheck.some((key) => data[key]?.length > 0);
  };

  const isProductLineFromRedux = data?.Product_Line?.length > 0;
  const otherFiltersApplied = isOtherFilterApplied(data);

  const finalProductLine = useMemo(() => {
    if (productLine) return [productLine];

    if (isProductLineFromRedux) {
      return data.Product_Line;
    }

    if (otherFiltersApplied) {
      // ✅ Fallback to dropdown if Redux Product_Line is empty
      return selectedOption ? [selectedOption] : [];
    }

    return selectedOption ? [selectedOption] : [];
  }, [
    data,
    isProductLineFromRedux,
    otherFiltersApplied,
    selectedOption,
    productLine,
  ]);

  const {
    data: rawData = [],
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["gtme", submitted, finalProductLine],
    queryFn: () => gtmeTableApi({ ...data, Product_Line: finalProductLine }),
    cacheTime: 0,
    staleTime: 0,
    // enabled: finalProductLine.length > 0,
  });

  const kpiKeys = useMemo(() => {
    if (!rawData.length) return [];
    return Object.keys(rawData[0]).filter(
      (key) => key !== "LOB" && typeof rawData[0][key] === "object",
    );
  }, [rawData]);

  const kpiRows = useMemo(() => {
    if (!rawData.length) return [];
    return kpiKeys.map((kpi) => {
      const monthValues = months.map((mon) => rawData[0][kpi]?.[mon] ?? "-");
      return { kpi, values: monthValues };
    });
  }, [rawData, kpiKeys, months]);

  const handleSelectionChange = (event) => {
    const selection = event.target.value;
    setSelectedOption(selection);
    setSelOps({ Product_Line: [selection] });
  };

  const fetchOptions = async (data) => {
    try {
      const response = await axInst.post("/dealer-review/gtme_filter", data);
      const dropdownOptions = response.data || [];
      setOptions(dropdownOptions);

      const shouldSetDefault = !isProductLineFromRedux && !otherFiltersApplied;

      if (shouldSetDefault && dropdownOptions.length > 0) {
        const defaultOption = "";

        setSelectedOption(defaultOption);
        setSelOps({ Product_Line: [defaultOption] });
      }

      if (isProductLineFromRedux) {
        setSelectedOption(data.Product_Line[0]);
      }
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    }
  };

  useEffect(() => {
    if (!productLine) fetchOptions(data);
  }, [submitted]);

  const kpiTargets = {
    "Beat Plan Adherence": 80,
    "Market Visibility": 90,
    "Nudges and PVIN score": 80,
    "TQP Conversion Velocity": 70,
    "C1-Retail Conversion %": 25,
  };

  return (
    <div className="bg-white rounded-md shadow-lg relative h-full ">
      <WaterMark />

      <div className="flex justify-between p-1 ">
        <div className="text-lg font-bold text-gray-800">
          GTME {productLine ? `- ${productLine}` : ""}
        </div>

        {/* Dropdown shown only if not in PDF mode */}
        {!productLine && (
          <select
            value={selectedOption || ""}
            onChange={handleSelectionChange}
            className="border border-gray-300 text-sm rounded-md w-40 p-1"
          >
            <option value="">All Product Line</option>
            {options.map((option, index) => (
              <option key={index} value={option.key}>
                {option.key}
              </option>
            ))}
          </select>
        )}
      </div>

      {isFetching ? (
        <div className="grid items-center justify-center w-full h-full">
          <Loader />
        </div>
      ) : isError || rawData.length === 0 || rawData?.message ? (
        <p className="text-center text-gray-600 py-4">
          Data Not Available or Please Select Relative Product Line
        </p>
      ) : (
        <div className="text-xs drilltable">
          <table className="min-w-full border text-left drilltable">
            <thead className="bg-[#28526E] text-white">
              <tr>
                <th className="border px-3 py-2 font-semibold"></th>
                {months.map((month) => (
                  <th
                    key={month}
                    className="border px-3 py-2 font-semibold text-center"
                  >
                    {month}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {kpiRows.map((row, idx) => {
                // format KPI name (remove underscores to match your target keys)
                const kpiName = row.kpi.replaceAll("_", " ");
                const target = kpiTargets[kpiName] || null;

                return (
                  <tr key={idx}>
                    <td className="border px-2 py-[5px] text-sm bg-[#D4E1EC] font-semibold">
                      {kpiName}
                      {target !== null && (
                        <span className="text-gray-700 text-xs font-normal ml-1">
                          (Target: {target}%)
                        </span>
                      )}
                    </td>
                    {row.values.map((val, i) => {
                      // Try to parse numeric (strip % if present)
                      const numericVal = parseFloat(
                        String(val).replace("%", ""),
                      );
                      const meetsTarget =
                        target !== null &&
                        !isNaN(numericVal) &&
                        numericVal >= target;

                      return (
                        <td
                          key={i}
                          className="border px-3 py-[5px] text-center"
                        >
                          <span
                            className={` text-xs p-1  rounded-full inline-block min-w-[50px] ${
                              target !== null && !isNaN(numericVal)
                                ? meetsTarget
                                  ? "bg-[#D1FAE5] text-[#06625B]"
                                  : "bg-[#FFF1F2] text-[#BE123C] "
                                : ""
                            }`}
                          >
                            {val}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GtmeTable;
